import React, { Component, ReactNode, ErrorInfo } from 'react';
import { Box, Typography, Button, Container, Paper } from '@mui/material';
import { ErrorOutline, Refresh } from '@mui/icons-material';
import { ErrorService } from '../../services/ErrorService';
import { AppError } from '../../types/errors';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: React.ComponentType<ErrorBoundaryFallbackProps>;
  onError?: (error: AppError, errorInfo: ErrorInfo) => void;
  showErrorDetails?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  processedError: AppError | null;
}

export interface ErrorBoundaryFallbackProps {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  processedError: AppError | null;
  onRetry: () => void;
  showErrorDetails: boolean;
}

/**
 * Default fallback component for error boundary
 */
const DefaultErrorFallback: React.FC<ErrorBoundaryFallbackProps> = ({
  processedError,
  onRetry,
  showErrorDetails,
  error,
  errorInfo,
}) => {
  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          textAlign: 'center',
          borderTop: 4,
          borderColor: 'error.main',
        }}
      >
        <ErrorOutline
          sx={{
            fontSize: 64,
            color: 'error.main',
            mb: 2,
          }}
        />

        <Typography variant="h4" component="h1" gutterBottom color="error.main">
          Something went wrong
        </Typography>

        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          {processedError?.message ||
            'An unexpected error occurred. Please try refreshing the page.'}
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Refresh />}
            onClick={onRetry}
            sx={{ mr: 2 }}
          >
            Retry
          </Button>

          <Button variant="outlined" color="secondary" onClick={() => (window.location.href = '/')}>
            Go Home
          </Button>
        </Box>

        {showErrorDetails && error && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mt: 3,
              backgroundColor: 'grey.50',
              textAlign: 'left',
            }}
          >
            <Typography variant="h6" gutterBottom>
              Error Details (Development)
            </Typography>

            <Typography variant="body2" component="pre" sx={{ mb: 2, fontSize: '0.75rem' }}>
              <strong>Error:</strong> {error.message}
            </Typography>

            {errorInfo && (
              <Typography
                variant="body2"
                component="pre"
                sx={{ fontSize: '0.75rem', overflow: 'auto' }}
              >
                <strong>Component Stack:</strong>
                {errorInfo.componentStack}
              </Typography>
            )}

            {error.stack && (
              <Typography
                variant="body2"
                component="pre"
                sx={{ fontSize: '0.75rem', overflow: 'auto', mt: 2 }}
              >
                <strong>Stack Trace:</strong>
                {error.stack}
              </Typography>
            )}
          </Paper>
        )}

        <Typography variant="caption" display="block" sx={{ mt: 3, color: 'text.disabled' }}>
          If this problem persists, please contact support with the error details above.
        </Typography>
      </Paper>
    </Container>
  );
};

/**
 * Global error boundary component that catches unhandled JavaScript errors
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      processedError: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Process the error using ErrorService
    const processedError = ErrorService.processError(error, {
      source: 'ERROR_BOUNDARY',
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
    });

    // Update state with error info and processed error
    this.setState({
      errorInfo,
      processedError,
    });

    // Call the onError callback if provided
    if (this.props.onError) {
      this.props.onError(processedError, errorInfo);
    }

    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by ErrorBoundary:', error);
      console.error('Component stack:', errorInfo.componentStack);
    }

    // Here you could also send the error to an error reporting service
    // like Sentry, LogRocket, or custom analytics
    this.reportError(processedError, errorInfo);
  }

  /**
   * Report error to external services
   */
  private reportError(error: AppError, errorInfo: ErrorInfo): void {
    // In a real application, you would send this to your error reporting service
    // For example:
    // Sentry.captureException(error.originalError, {
    //   contexts: {
    //     errorBoundary: {
    //       componentStack: errorInfo.componentStack,
    //       ...error.context,
    //     },
    //   },
    // });

    // For now, just log it
    console.error('Reporting error to external service:', {
      error,
      errorInfo,
    });
  }

  /**
   * Retry handler - resets the error boundary state
   */
  private handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      processedError: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback || DefaultErrorFallback;

      return (
        <FallbackComponent
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          processedError={this.state.processedError}
          onRetry={this.handleRetry}
          showErrorDetails={this.props.showErrorDetails ?? process.env.NODE_ENV === 'development'}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * HOC (Higher-Order Component) for wrapping components with error boundary
 */
// eslint-disable-next-line react-refresh/only-export-components
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
}

/**
 * Hook for manually triggering error boundary (useful for async errors)
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useErrorBoundary() {
  const [, setState] = React.useState();

  return React.useCallback((error: Error) => {
    setState(() => {
      throw error;
    });
  }, []);
}

export default ErrorBoundary;
